import React, { useState } from 'react';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

/**
 * ChangeProposalCard - Displays proposed changes from the chat agent
 *
 * Renders a list of changes with checkboxes for selective approval.
 * Users can approve all, select specific changes, or reject the entire proposal.
 */
const ChangeProposalCard = ({
  proposalId,
  changes,
  status = 'pending',
  onApprove,
  onReject,
  loading = false,
}) => {
  // Track which changes are selected (all selected by default)
  const [selectedIndices, setSelectedIndices] = useState(
    changes.map((_, i) => i)
  );
  // Track which changes are expanded to show details
  const [expandedIndices, setExpandedIndices] = useState([]);

  // Toggle expanded state for a change
  const toggleExpanded = (index, e) => {
    e.stopPropagation();
    setExpandedIndices(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Toggle a single change selection
  const toggleChange = (index) => {
    setSelectedIndices(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Select all changes
  const selectAll = () => {
    setSelectedIndices(changes.map((_, i) => i));
  };

  // Deselect all changes
  const selectNone = () => {
    setSelectedIndices([]);
  };

  // Get icon for artifact type
  const getArtifactIcon = (artifactType) => {
    const icons = {
      feature: '📚',
      story: '📖',
      functional_requirement: '✅',
      non_functional_requirement: '⚙️',
      task: '📋',
      event: '⚡',
      command: '▶️',
      read_model: '👁️',
    };
    return icons[artifactType] || '📄';
  };

  // Get color for change type
  const getChangeColor = (changeType) => {
    const colors = {
      add: T.moss,
      modify: T.orange,
      remove: T.capRed,
    };
    return colors[changeType] || T.inkMuted;
  };

  // Get label for change type
  const getChangeLabel = (changeType) => {
    const labels = {
      add: 'Add',
      modify: 'Modify',
      remove: 'Remove',
    };
    return labels[changeType] || changeType;
  };

  // Format field name for display (snake_case to Title Case)
  const formatFieldName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Handle approve click
  const handleApprove = () => {
    if (onApprove && selectedIndices.length > 0) {
      onApprove(proposalId, selectedIndices);
    }
  };

  // Handle reject click
  const handleReject = () => {
    if (onReject) {
      onReject(proposalId);
    }
  };

  // Render based on status
  if (status === 'approved') {
    return (
      <div style={{
        marginTop: '12px',
        padding: '12px 16px',
        backgroundColor: `${T.moss}12`,
        border: `1px solid ${T.moss}40`,
        borderRadius: T.r3,
        boxShadow: T.sh1,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: 700,
          color: T.moss,
          fontFamily: T.sans,
        }}>
          <span>&#10003;</span>
          <span>Changes Applied</span>
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: T.inkMuted,
          fontFamily: T.sans,
        }}>
          {changes.length} change{changes.length !== 1 ? 's' : ''} applied
        </div>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div style={{
        marginTop: '12px',
        padding: '12px 16px',
        backgroundColor: `${T.capRed}10`,
        border: `1px solid ${T.capRed}35`,
        borderRadius: T.r3,
        opacity: 0.7,
        boxShadow: T.sh1,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: 700,
          color: T.capRed,
          fontFamily: T.sans,
        }}>
          <span>&#10005;</span>
          <span>Proposal Rejected</span>
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: T.inkMuted,
          fontFamily: T.sans,
        }}>
          {changes.length} change{changes.length !== 1 ? 's' : ''} rejected
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      backgroundColor: T.card,
      border: `1px solid ${T.rule}`,
      borderRadius: T.r3,
      overflow: 'hidden',
      boxShadow: T.sh1,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderBottom: `1px solid ${T.rule}`,
        backgroundColor: T.paperDeep,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '14px' }}>📝</span>
          <span style={{
            fontSize: '13px',
            fontWeight: 700,
            color: T.ink,
            fontFamily: T.sans,
          }}>
            Proposed Changes
          </span>
          <span style={{
            fontSize: '11px',
            color: T.inkFaint,
            padding: '1px 8px',
            backgroundColor: `rgba(46,33,9,0.07)`,
            borderRadius: T.full,
            fontFamily: T.sans,
          }}>
            {changes.length} change{changes.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={selectAll}
            disabled={loading}
            style={{
              padding: '3px 10px',
              fontSize: '11px',
              color: T.inkMuted,
              backgroundColor: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2,
              cursor: 'pointer',
              fontFamily: T.sans,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.ruleStrong; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; }}
          >
            All
          </button>
          <button
            onClick={selectNone}
            disabled={loading}
            style={{
              padding: '3px 10px',
              fontSize: '11px',
              color: T.inkMuted,
              backgroundColor: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2,
              cursor: 'pointer',
              fontFamily: T.sans,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.ruleStrong; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; }}
          >
            None
          </button>
        </div>
      </div>

      {/* Change List */}
      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
        {changes.map((change, index) => (
          <div key={index}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                transition: 'background-color 0.12s ease',
                borderBottom: `1px solid ${T.rule}`,
                backgroundColor: selectedIndices.includes(index) ? `${T.orange}0E` : 'transparent',
              }}
              onClick={() => !loading && toggleChange(index)}
            >
              <input
                type="checkbox"
                checked={selectedIndices.includes(index)}
                onChange={(e) => { e.stopPropagation(); toggleChange(index); }}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer',
                  accentColor: T.orange,
                }}
                disabled={loading}
              />
              <span style={{ fontSize: '13px' }}>
                {getArtifactIcon(change.artifact_type)}
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: getChangeColor(change.change_type),
                minWidth: '50px',
                fontFamily: T.sans,
                letterSpacing: '0.04em',
              }}>
                {getChangeLabel(change.change_type)}
              </span>
              <span style={{
                flex: 1,
                fontSize: '13px',
                color: T.ink2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: T.sans,
              }}>
                {change.summary}
              </span>
              <button
                onClick={(e) => toggleExpanded(index, e)}
                title={expandedIndices.includes(index) ? 'Hide details' : 'Show details'}
                style={{
                  padding: '3px 6px',
                  fontSize: '11px',
                  color: T.inkFaint,
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  marginLeft: 'auto',
                  fontFamily: T.sans,
                  transition: 'color 0.12s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.inkMuted; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.inkFaint; }}
              >
                {expandedIndices.includes(index) ? '▼' : '▶'}
              </button>
            </div>

            {/* Expanded details */}
            {expandedIndices.includes(index) && (
              <div style={{
                padding: '10px 16px 10px 48px',
                backgroundColor: T.paperSoft,
                borderBottom: `1px solid ${T.rule}`,
              }}>
                {change.change_type === 'add' && change.new_data && (
                  <div style={{ fontSize: '13px' }}>
                    <div style={{
                      fontWeight: 700,
                      color: T.ink,
                      marginBottom: '8px',
                      fontFamily: T.sans,
                    }}>
                      Adding:
                    </div>
                    {Object.entries(change.new_data).map(([key, value]) => (
                      <div key={key} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        padding: '3px 0',
                        flexWrap: 'wrap',
                        backgroundColor: `${T.moss}0D`,
                        borderLeft: `2px solid ${T.moss}`,
                        paddingLeft: '8px',
                        marginBottom: '3px',
                        borderRadius: '0 4px 4px 0',
                      }}>
                        <span style={{ color: T.inkMuted, minWidth: '120px', fontWeight: 600, fontFamily: T.sans }}>
                          {formatFieldName(key)}:
                        </span>
                        <span style={{ color: T.ink2, flex: 1, wordBreak: 'break-word', fontFamily: T.sans }}>
                          {formatValue(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {change.change_type === 'modify' && change.field_changes && (
                  <div style={{ fontSize: '13px' }}>
                    <div style={{
                      fontWeight: 700,
                      color: T.ink,
                      marginBottom: '8px',
                      fontFamily: T.sans,
                    }}>
                      Changing:
                    </div>
                    {Object.entries(change.field_changes).map(([field, vals]) => (
                      <div key={field} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        padding: '3px 0',
                        flexWrap: 'wrap',
                      }}>
                        <span style={{ color: T.inkMuted, minWidth: '120px', fontWeight: 600, fontFamily: T.sans }}>
                          {formatFieldName(field)}:
                        </span>
                        <span style={{
                          color: T.capRed,
                          textDecoration: 'line-through',
                          backgroundColor: `${T.capRed}0D`,
                          padding: '0 4px',
                          borderRadius: T.r2,
                          fontFamily: T.sans,
                        }}>
                          {formatValue(vals.old) || '(empty)'}
                        </span>
                        <span style={{ color: T.inkFaint, padding: '0 4px', fontFamily: T.sans }}>→</span>
                        <span style={{
                          color: T.moss,
                          fontWeight: 600,
                          backgroundColor: `${T.moss}0D`,
                          padding: '0 4px',
                          borderRadius: T.r2,
                          fontFamily: T.sans,
                        }}>
                          {formatValue(vals.new)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {change.change_type === 'remove' && (
                  <div style={{ fontSize: '13px' }}>
                    <div style={{
                      fontWeight: 700,
                      color: T.capRed,
                      fontFamily: T.sans,
                      backgroundColor: `${T.capRed}0D`,
                      borderLeft: `2px solid ${T.capRed}`,
                      padding: '6px 8px',
                      borderRadius: '0 4px 4px 0',
                    }}>
                      Removing {change.artifact_type}: {change.artifact_id}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        padding: '10px 16px',
        backgroundColor: T.paperDeep,
        borderTop: `1px solid ${T.rule}`,
      }}>
        <button
          onClick={handleReject}
          disabled={loading}
          style={{
            padding: '7px 16px',
            fontSize: '13px',
            fontWeight: 600,
            color: T.capRed,
            backgroundColor: 'transparent',
            border: `1px solid ${T.capRed}60`,
            borderRadius: T.r3,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: T.sans,
            transition: 'all 0.12s ease',
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = `${T.capRed}10`;
              e.currentTarget.style.borderColor = T.capRed;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = `${T.capRed}60`;
          }}
        >
          Reject All
        </button>
        <button
          onClick={handleApprove}
          disabled={loading || selectedIndices.length === 0}
          style={{
            padding: '7px 16px',
            fontSize: '13px',
            fontWeight: 700,
            color: '#fff',
            backgroundColor: selectedIndices.length === 0 ? T.inkFaint : T.moss,
            border: 'none',
            borderRadius: T.r3,
            cursor: (loading || selectedIndices.length === 0) ? 'not-allowed' : 'pointer',
            fontFamily: T.sans,
            transition: 'background-color 0.12s ease',
            opacity: loading ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!loading && selectedIndices.length > 0) e.currentTarget.style.backgroundColor = '#536030';
          }}
          onMouseLeave={(e) => {
            if (selectedIndices.length > 0) e.currentTarget.style.backgroundColor = T.moss;
          }}
        >
          {loading ? (
            'Applying...'
          ) : (
            `Apply ${selectedIndices.length} Change${selectedIndices.length !== 1 ? 's' : ''}`
          )}
        </button>
      </div>
    </div>
  );
};

export default ChangeProposalCard;
